import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useApplyBranding } from '@/features/branding/useBranding';
import { FullScreenLoader, ProtectedRoute, RequirePermission } from '@/features/auth/ProtectedRoute';
import { useAuthBootstrap } from '@/features/auth/useSession';
import { useApplyTheme, useResolvedTheme } from '@/shared/theme/useTheme';
import { AppLayout } from './layout/AppLayout';
import { NAV_ITEMS } from './layout/nav';

// Route-level code splitting: each page is its own chunk.
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const SurveysPage = lazy(() => import('@/features/surveys/SurveysPage'));
const SurveyEditorPage = lazy(() => import('@/features/surveys/SurveyEditorPage'));
const SurveyResultsPage = lazy(() => import('@/features/surveys/SurveyResultsPage'));
const SetPasswordPage = lazy(() => import('@/features/account/SetPasswordPage'));
const AccountPage = lazy(() => import('@/features/account/AccountPage'));
const MyDayPage = lazy(() => import('@/features/my-day/MyDayPage'));
const TasksPage = lazy(() => import('@/features/tasks/TasksPage'));
const VisitsPage = lazy(() => import('@/features/visits/VisitsPage'));
const LogisticsTodayPage = lazy(() => import('@/features/logistics/TodayPage'));
const LogisticsDeliveriesPage = lazy(() => import('@/features/logistics/DeliveriesPage'));
const LogisticsProblemsPage = lazy(() => import('@/features/logistics/ProblemsPage'));
const LogisticsRoutesPage = lazy(() => import('@/features/logistics/RoutesPage'));
const LogisticsRouteDetailPage = lazy(() => import('@/features/logistics/RouteDetailPage'));
const LogisticsOptimizationPage = lazy(() => import('@/features/logistics/OptimizationPage'));
const LogisticsFulfilmentPage = lazy(() => import('@/features/logistics/FulfilmentPage'));
const LogisticsReportsPage = lazy(() => import('@/features/logistics/reports/ReportsPage'));
const BoardPage = lazy(() => import('@/features/analytics/pages/BoardPage'));
const TeamPage = lazy(() => import('@/features/analytics/pages/TeamPage'));
const RepPage = lazy(() => import('@/features/analytics/pages/RepPage'));
const RepSalesPage = lazy(() => import('@/features/rep-sales/RepSalesPage'));
const AiUsagePage = lazy(() => import('@/features/ai/admin/AiUsagePage'));
const OwnerDashboardPage = lazy(() => import('@/features/owner-dashboard/OwnerDashboardPage'));
const MbrPage = lazy(() => import('@/features/mbr/MbrPage'));
const InsuranceDashboardPage = lazy(() => import('@/features/insurance/dashboard/InsuranceDashboardPage'));
const InsurancePoliciesPage = lazy(() => import('@/features/insurance/policies/PoliciesPage'));
const InsuranceImportPage = lazy(() => import('@/features/insurance/policies/ImportPage'));
const InsuranceQueuePage = lazy(() => import('@/features/insurance/claims/QueuePage'));
const InsuranceClaimsPage = lazy(() => import('@/features/insurance/claims/ClaimsPage'));
const InsuranceClaimDetailPage = lazy(() => import('@/features/insurance/claims/ClaimDetailPage'));
const InsuranceTendersPage = lazy(() => import('@/features/insurance/tenders/TendersPage'));
const InsuranceTenderDetailPage = lazy(() => import('@/features/insurance/tenders/TenderDetailPage'));
const InsuranceAnalyticsPage = lazy(() => import('@/features/insurance/analytics/AnalyticsPage'));
const InsuranceSettingsPage = lazy(() => import('@/features/insurance/settings/InsuranceSettingsPage'));
const PlannerPage = lazy(() => import('@/features/planner/PlannerPage'));
const PlannerSetupPage = lazy(() => import('@/features/planner/setup/PlannerSetupPage'));
const CollectionsPage = lazy(() => import('@/features/collections/CollectionsPage'));
const WorktimePage = lazy(() => import('@/features/worktime/WorktimePage'));
const MyWorktimePage = lazy(() => import('@/features/worktime/MyWorktimePage'));
const WorktimeSettingsPage = lazy(() => import('@/features/worktime/WorktimeSettingsPage'));
const LeavePage = lazy(() => import('@/features/leave/LeavePage'));
const CashflowPage = lazy(() => import('@/features/cashflow/CashflowPage'));
const QualityOverviewPage = lazy(() => import('@/features/quality/OverviewPage'));
const QualityComplaintsPage = lazy(() => import('@/features/quality/ComplaintsPage'));
const QualityAnalyticsPage = lazy(() => import('@/features/quality/AnalyticsPage'));
const QualityEventsPage = lazy(() => import('@/features/quality/EventsPage'));
const QualitySuppliersPage = lazy(() => import('@/features/quality/SuppliersPage'));
const WarehousePage = lazy(() => import('@/features/warehouse/WarehousePage'));
const SettingsPage = lazy(() => import('@/features/admin/settings/SettingsPage'));
const DictionariesPage = lazy(() => import('@/features/dictionaries/DictionariesPage'));
const NotificationsPage = lazy(() => import('@/features/notifications/NotificationsPage'));
const ChatPage = lazy(() => import('@/features/chat/ChatPage'));
const LeadsPage = lazy(() => import('@/features/leads/LeadsPage'));
const WorkflowPage = lazy(() => import('@/features/workflow/WorkflowPage'));
const RequestDetailPage = lazy(() => import('@/features/workflow/RequestDetailPage'));
const NewCustomerPage = lazy(() => import('@/features/workflow/wizard/NewCustomerWizard'));
const CustomersPage = lazy(() => import('@/features/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('@/features/customers/CustomerDetailPage'));
const InvoiceComparisonPage = lazy(() => import('@/features/invoice-compare/InvoiceComparisonPage'));
const ModulePlaceholder = lazy(() => import('@/features/placeholders/routes').then((m) => ({ default: m.ModulePlaceholder })));
const AnalyticsPlaceholder = lazy(() =>
  import('@/features/placeholders/routes').then((m) => ({ default: m.AnalyticsPlaceholder })),
);
const MarginPage = lazy(() => import('@/features/analytics-finance/pages/MarginPage'));
const ProfitabilityPage = lazy(() => import('@/features/analytics-finance/pages/ProfitabilityPage'));
const RouteDetailPage = lazy(() => import('@/features/analytics-finance/pages/RouteDetailPage'));
const ReceivablesPage = lazy(() => import('@/features/analytics-finance/pages/ReceivablesPage'));
const ForecastsPage = lazy(() => import('@/features/analytics-finance/pages/ForecastsPage'));
const PurchasingPage = lazy(() => import('@/features/purchasing/PurchasingPage'));
const ActivityPage = lazy(() => import('@/features/analytics-ops/activity/ActivityPage'));
const FunnelPage = lazy(() => import('@/features/analytics-ops/funnel/FunnelPage'));
const GdprPage = lazy(() => import('@/features/gdpr/GdprPage'));
const TeamReportsPage = lazy(() => import('@/features/daily-reports/TeamReportsPage'));
const MyDailyReportPage = lazy(() => import('@/features/daily-reports/MyDailyReportPage'));
const BonusPage = lazy(() => import('@/features/analytics-ops/bonus/BonusPage'));
const CustomersAnalyticsPage = lazy(() => import('@/features/analytics-customers/pages/CustomersAnalyticsPage'));
const ProductsAnalyticsPage = lazy(() => import('@/features/analytics-products/pages/ProductsAnalyticsPage'));
const AdminLivePage = lazy(() => import('@/features/admin-live/AdminLivePage'));
const UsersPage = lazy(() => import('@/features/admin/users/UsersPage'));
const RolesPage = lazy(() => import('@/features/admin/roles/RolesPage'));
const AuditPage = lazy(() => import('@/features/admin/audit/AuditPage'));
const ErpSyncPage = lazy(() => import('@/features/integrations/ErpSyncPage'));
const ErpSyncRunPage = lazy(() => import('@/features/integrations/ErpSyncRunPage'));
const ErpOutboxPage = lazy(() => import('@/features/integrations/ErpOutboxPage'));
const LegalPage = lazy(() => import('@/features/legal/LegalPage'));
const HelpPage = lazy(() => import('@/features/help/HelpPage'));
const FleetIndexRedirect = lazy(() => import('@/features/fleet/FleetIndexRedirect'));
const VehiclesPage = lazy(() => import('@/features/fleet/VehiclesPage'));
const VehicleDetailPage = lazy(() => import('@/features/fleet/VehicleDetailPage'));
const LeasesPage = lazy(() => import('@/features/fleet/LeasesPage'));
const LeaseDetailPage = lazy(() => import('@/features/fleet/LeaseDetailPage'));
const LogbooksPage = lazy(() => import('@/features/fleet/logbooks/LogbooksPage'));
const MyLogbooksPage = lazy(() => import('@/features/fleet/logbooks/MyLogbooksPage'));
const LogbookDetailPage = lazy(() => import('@/features/fleet/logbooks/LogbookDetailPage'));
const MyLogbookDetailPage = lazy(() => import('@/features/fleet/logbooks/LogbookDetailPage').then((m) => ({ default: m.MyLogbookDetailPage })));
const FleetSettingsPage = lazy(() => import('@/features/fleet/FleetSettingsPage'));
const FuelQueuePage = lazy(() => import('@/features/fleet/ReviewQueuePage').then((m) => ({ default: m.FuelQueuePage })));
const ExpensesQueuePage = lazy(() => import('@/features/fleet/ReviewQueuePage').then((m) => ({ default: m.ExpensesQueuePage })));
const FuelDetailPage = lazy(() => import('@/features/fleet/ReviewDetailPages').then((m) => ({ default: m.FuelDetailPage })));
const ExpenseDetailPage = lazy(() => import('@/features/fleet/ReviewDetailPages').then((m) => ({ default: m.ExpenseDetailPage })));
const IncidentsPage = lazy(() => import('@/features/fleet/IncidentsPage'));
const IncidentDetailPage = lazy(() => import('@/features/fleet/IncidentDetailPage'));
const ChecklistRunsPage = lazy(() => import('@/features/fleet/ChecklistRunsPage'));
const ChecklistRunDetailPage = lazy(() => import('@/features/fleet/ChecklistRunDetailPage'));
const TemplatesPage = lazy(() => import('@/features/fleet/TemplatesPage'));
const ServicePage = lazy(() => import('@/features/fleet/ServicePage'));
const FleetAnalyticsPage = lazy(() => import('@/features/fleet/FleetAnalyticsPage'));
const RoutesPage = lazy(() => import('@/features/fleet/RoutesPage'));
const FleetRouteDetailPage = lazy(() => import('@/features/fleet/RouteDetailPage'));
const IntegrationPage = lazy(() => import('@/features/fleet/IntegrationPage'));
const SyncRunDetailPage = lazy(() => import('@/features/fleet/SyncRunDetailPage'));
const FleetAiPage = lazy(() => import('@/features/fleet/ai/FleetAiPage'));
const CostsPage = lazy(() => import('@/features/costs/CostsPage'));
const CommissionsPage = lazy(() => import('@/features/commissions/CommissionsPage'));
const SalesOrdersPage = lazy(() => import('@/features/sales-orders/SalesOrdersPage'));
const NotFoundPage = lazy(() => import('@/features/placeholders/PlaceholderPage').then((m) => ({ default: m.NotFoundPage })));

/** App root inside the router: theme, branding, session bootstrap, toasts. */
function Root() {
  useApplyTheme();
  useApplyBranding();
  useAuthBootstrap();
  const theme = useResolvedTheme();
  return (
    <>
      <Suspense fallback={<FullScreenLoader />}>
        <Outlet />
      </Suspense>
      <Toaster theme={theme} position="top-right" richColors closeButton toastOptions={{ className: 'text-[13px]' }} />
    </>
  );
}

/** Legacy `/alerts?…` links land in the notification centre's alerts tab, keeping the filters. */
function AlertsRedirect() {
  const { search } = useLocation();
  const rest = new URLSearchParams(search);
  rest.delete('tab');
  const extra = rest.toString();
  return <Navigate to={`/notifications?tab=alerts${extra ? `&${extra}` : ''}`} replace />;
}

const placeholder = (key: string) => ({ path: NAV_ITEMS.find((i) => i.key === key)!.to.slice(1), element: <ModulePlaceholder navKey={key} /> });

export const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/set-password', element: <SetPasswordPage /> },
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: '/',
            element: <AppLayout />,
            children: [
              { index: true, element: <MyDayPage /> },
              { path: 'customers', element: <CustomersPage /> },
              { path: 'customers/:id', element: <CustomerDetailPage /> },
              { path: 'invoice-comparisons/:id', element: <InvoiceComparisonPage /> },
              { path: 'leads', element: <LeadsPage /> },
              { path: 'visits', element: <VisitsPage /> },
              { path: 'tasks', element: <TasksPage /> },
              { path: 'my-daily-report', element: <MyDailyReportPage /> },
              { path: 'notifications', element: <NotificationsPage /> },
              { path: 'alerts', element: <AlertsRedirect /> },
              { path: 'chat/:conversationId?', element: <ChatPage /> },
              { path: 'workflow', element: <WorkflowPage /> },
              { path: 'workflow/new', element: <NewCustomerPage /> },
              { path: 'workflow/requests/:id', element: <RequestDetailPage /> },
              { path: 'logistics', element: <LogisticsTodayPage /> },
              { path: 'logistics/deliveries', element: <LogisticsDeliveriesPage /> },
              { path: 'logistics/routes', element: <LogisticsRoutesPage /> },
              { path: 'logistics/routes/:id', element: <LogisticsRouteDetailPage /> },
              { path: 'logistics/problems', element: <LogisticsProblemsPage /> },
              { path: 'logistics/fulfilment', element: <LogisticsFulfilmentPage /> },
              { path: 'logistics/optimization', element: <LogisticsOptimizationPage /> },
              { path: 'logistics/reports', element: <LogisticsReportsPage /> },
              { path: 'fleet', element: <FleetIndexRedirect /> },
              { path: 'fleet/vehicles', element: <VehiclesPage /> },
              { path: 'fleet/vehicles/:id', element: <VehicleDetailPage /> },
              { path: 'fleet/leases', element: <LeasesPage /> },
              { path: 'fleet/leases/:id', element: <LeaseDetailPage /> },
              { path: 'fleet/logbooks', element: <LogbooksPage /> },
              { path: 'fleet/logbooks/:id', element: <LogbookDetailPage /> },
              { path: 'fleet/my-logbooks', element: <MyLogbooksPage /> },
              { path: 'fleet/my-logbooks/:id', element: <MyLogbookDetailPage /> },
              { path: 'fleet/fuel', element: <FuelQueuePage /> },
              { path: 'fleet/fuel/:id', element: <FuelDetailPage /> },
              { path: 'fleet/expenses', element: <ExpensesQueuePage /> },
              { path: 'fleet/expenses/:id', element: <ExpenseDetailPage /> },
              { path: 'fleet/incidents', element: <IncidentsPage /> },
              { path: 'fleet/incidents/:id', element: <IncidentDetailPage /> },
              { path: 'fleet/checklists', element: <ChecklistRunsPage /> },
              { path: 'fleet/checklists/:id', element: <ChecklistRunDetailPage /> },
              { path: 'fleet/service', element: <ServicePage /> },
              { path: 'fleet/templates', element: <TemplatesPage /> },
              { path: 'fleet/settings', element: <FleetSettingsPage /> },
              { path: 'fleet/analytics', element: <FleetAnalyticsPage /> },
              { path: 'fleet/routes', element: <RoutesPage /> },
              { path: 'fleet/routes/:id', element: <FleetRouteDetailPage /> },
              { path: 'fleet/integration', element: <IntegrationPage /> },
              { path: 'fleet/integration/runs/:id', element: <SyncRunDetailPage /> },
              { path: 'fleet/insights', element: <FleetAiPage /> },
              // Ubezpieczenia i szkody — każda strona sama sprawdza swoje uprawnienie (RequirePermission w pliku strony).
              { path: 'planner', element: <PlannerPage /> },
              { path: 'planner/setup', element: <PlannerSetupPage /> },
              { path: 'worktime', element: <WorktimePage /> },
              { path: 'worktime/my', element: <MyWorktimePage /> },
              { path: 'worktime/settings', element: <WorktimeSettingsPage /> },
              { path: 'leave', element: <LeavePage /> },
              { path: 'warehouse', element: <WarehousePage /> },
              { path: 'insurance', element: <InsuranceDashboardPage /> },
              { path: 'insurance/policies', element: <InsurancePoliciesPage /> },
              { path: 'insurance/import', element: <InsuranceImportPage /> },
              { path: 'insurance/queue', element: <InsuranceQueuePage /> },
              { path: 'insurance/claims', element: <InsuranceClaimsPage /> },
              { path: 'insurance/claims/:id', element: <InsuranceClaimDetailPage /> },
              { path: 'insurance/tenders', element: <InsuranceTendersPage /> },
              { path: 'insurance/tenders/:id', element: <InsuranceTenderDetailPage /> },
              { path: 'insurance/analytics', element: <InsuranceAnalyticsPage /> },
              { path: 'insurance/settings', element: <InsuranceSettingsPage /> },
              { path: 'costs', element: <CostsPage /> },
              { path: 'finance/cashflow', element: <CashflowPage /> },
              // Jakość — strony same sprawdzają uprawnienie (quality.view / quality.events).
              { path: 'quality', element: <QualityOverviewPage /> },
              { path: 'quality/complaints', element: <QualityComplaintsPage /> },
              { path: 'quality/analytics', element: <QualityAnalyticsPage /> },
              { path: 'quality/events', element: <QualityEventsPage /> },
              { path: 'quality/suppliers', element: <QualitySuppliersPage /> },
              { path: 'admin/gdpr', element: <GdprPage /> },
              ...['consents', 'feedback'].map(placeholder),
              { path: 'manager', element: <RequirePermission permission="dashboard.owner"><OwnerDashboardPage /></RequirePermission> },
              { path: 'mbr', element: <RequirePermission permission={['mbr.read', 'mbr.write']}><MbrPage /></RequirePermission> },
              { path: 'admin/settings', element: <SettingsPage /> },
              { path: 'admin/dictionaries', element: <DictionariesPage /> },
              { path: 'account', element: <AccountPage /> },
              { path: 'legal', element: <LegalPage /> },
              { path: 'help', element: <HelpPage /> },
              { path: 'admin/users', element: <UsersPage /> },
              { path: 'admin/roles', element: <RolesPage /> },
              { path: 'admin/audit', element: <AuditPage /> },
              { path: 'admin/ai', element: <AiUsagePage /> },
              // Integracje (ERP): każda strona sama sprawdza erp.sync.view.
              { path: 'integrations/erp-sync', element: <ErpSyncPage /> },
              { path: 'integrations/erp-sync/runs/:id', element: <ErpSyncRunPage /> },
              { path: 'integrations/erp-outbox', element: <ErpOutboxPage /> },
              { path: 'analytics', element: <AnalyticsPlaceholder /> },
              { path: 'analytics/board', element: <BoardPage /> },
              { path: 'analytics/customers', element: <CustomersAnalyticsPage /> },
              { path: 'analytics/products', element: <ProductsAnalyticsPage /> },
              { path: 'analytics/team', element: <TeamPage /> },
              { path: 'analytics/rep', element: <RepPage /> },
              { path: 'analytics/rep-sales', element: <RepSalesPage /> },
              // Notification links of the weekly sending point at the app route — open the panel view instead.
              { path: 'results/rep-sales', element: <Navigate to="/analytics/rep-sales" replace /> },
              { path: 'analytics/activity', element: <ActivityPage /> },
              { path: 'analytics/funnel', element: <FunnelPage /> },
              { path: 'analytics/team-reports', element: <TeamReportsPage /> },
              { path: 'analytics/bonus', element: <BonusPage /> },
              { path: 'admin/live', element: <AdminLivePage /> },
              { path: 'analytics/margin', element: <MarginPage /> },
              { path: 'analytics/profitability', element: <ProfitabilityPage /> },
              { path: 'analytics/profitability/routes/:id', element: <RouteDetailPage /> },
              { path: 'analytics/receivables', element: <ReceivablesPage /> },
              { path: 'analytics/forecasts', element: <ForecastsPage /> },
              { path: 'finance/collections', element: <CollectionsPage /> },
              { path: 'purchasing', element: <PurchasingPage /> },
              { path: 'analytics/:section', element: <AnalyticsPlaceholder /> },
              { path: 'sales/commissions', element: <CommissionsPage /> },
              { path: 'sales/orders', element: <SalesOrdersPage /> },
              // Ankiety klientów — strony same sprawdzają surveys.manage.
              { path: 'surveys', element: <SurveysPage /> },
              { path: 'surveys/new', element: <SurveyEditorPage /> },
              { path: 'surveys/:id', element: <SurveyEditorPage /> },
              { path: 'surveys/:id/results', element: <SurveyResultsPage /> },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
